import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_KIOSK_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/kiosk"
).replace(/\/+$/, "");

const isValidStorageValue = (value) =>
  value !== null && value !== undefined && value !== "" && value !== "undefined" && value !== "null";

const parseKiosks = (value) => {
  const source = Array.isArray(value) ? value : String(value || "").split(",");
  return [...new Set(source.map((item) => String(item || "").trim()).filter(Boolean))];
};

const SelectionCard = ({ active, title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-2xl border-4 px-8 py-7 text-left transition ${
      active ? "border-blue-600 bg-blue-50 shadow-xl" : "border-gray-200 bg-white hover:border-blue-300"
    }`}
  >
    <span className="block text-3xl font-extrabold text-gray-950">{title}</span>
    {subtitle ? <span className="mt-2 block text-xl text-gray-600">{subtitle}</span> : null}
  </button>
);

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [step, setStep] = useState("login");
  const [pendingLogin, setPendingLogin] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedKiosk, setSelectedKiosk] = useState("");

  const selectedBranch = useMemo(
    () => branches.find((branch) => String(branch.subeId) === String(selectedBranchId)) || null,
    [branches, selectedBranchId]
  );
  const availableKiosks = useMemo(() => parseKiosks(selectedBranch?.kiosks), [selectedBranch]);

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    const card = localStorage.getItem("card");
    const kiosk = localStorage.getItem("kiosk");
    const subeId = localStorage.getItem("subeId");
    if ([token, card, kiosk, subeId].every(isValidStorageValue)) {
      navigate("/");
      return;
    }
    ["userToken", "card", "kiosk", "subeId", "branchName"].forEach((key) => localStorage.removeItem(key));
  }, [navigate]);

  const fetchBranches = async (token, card) => {
    const response = await fetch(`${API_BASE_URL}/branches`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ card }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) throw new Error(data?.message || "Şubeler alınamadı");
    return Array.isArray(data.branches) ? data.branches : Array.isArray(data.data) ? data.data : [];
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !data?.token || !data?.card) {
        throw new Error(data?.message || "Email veya şifre yanlış");
      }
      const branchRows = await fetchBranches(data.token, data.card);
      if (!branchRows.length) throw new Error("Bu hesaba bağlı şube bulunamadı.");
      setPendingLogin({ token: data.token, card: data.card });
      setBranches(branchRows);
      setSelectedBranchId(branchRows.length === 1 ? String(branchRows[0].subeId || "") : "");
      setSelectedKiosk("");
      setStep("branch");
    } catch (requestError) {
      setError(requestError?.message || "Sunucuya bağlanılamadı");
    } finally {
      setLoading(false);
    }
  };

  const goToKioskStep = () => {
    if (!selectedBranch) {
      setError("Lütfen bir şube seçin.");
      return;
    }
    const kiosks = parseKiosks(selectedBranch.kiosks);
    if (!kiosks.length) {
      setError("Seçilen şubeye tanımlı kiosk bulunamadı.");
      return;
    }
    setSelectedKiosk(kiosks.length === 1 ? kiosks[0] : "");
    setError("");
    setStep("kiosk");
  };

  const confirmKiosk = () => {
    if (!selectedKiosk || !selectedBranch || !pendingLogin?.token || !pendingLogin?.card) {
      setError("Şube ve kiosk seçimi eksik.");
      return;
    }
    localStorage.clear();
    localStorage.setItem("userToken", pendingLogin.token);
    localStorage.setItem("card", pendingLogin.card);
    localStorage.setItem("subeId", String(selectedBranch.subeId));
    localStorage.setItem("branchName", String(selectedBranch.name || ""));
    localStorage.setItem("kiosk", selectedKiosk);
    localStorage.setItem("selectedOrderType", "reservation");
    navigate("/");
  };

  const page = (title, children) => (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-start pt-8 pb-20 px-4">
      <div className="mx-auto w-full max-w-4xl">
        <h1 className="text-center text-6xl font-extrabold text-gray-950 mb-12">{title}</h1>
        <div className="rounded-3xl bg-white px-12 py-14 shadow-2xl">
          {error ? <div className="mb-8 rounded-2xl border-2 border-red-300 bg-red-50 px-7 py-5 text-2xl font-bold text-red-700">{error}</div> : null}
          {children}
        </div>
      </div>
    </div>
  );

  if (showForgotPassword) {
    return page("Şifre Sıfırlama", (
      <div className="text-center">
        <p className="text-3xl text-gray-700">Şifre değişikliği için kayıtlı e-posta adresinizden</p>
        <p className="my-6 text-4xl font-extrabold">companyfrostyway@gmail.com</p>
        <button type="button" onClick={() => setShowForgotPassword(false)} className="mt-8 w-full rounded-2xl bg-blue-600 py-6 text-3xl font-bold text-white">Giriş Sayfasına Dön</button>
      </div>
    ));
  }

  if (step === "branch") {
    return page("Şube Seçimi", (
      <>
        <p className="mb-8 text-center text-2xl text-gray-600">Kioskun bulunduğu şubeyi seçin.</p>
        <div className="space-y-5">
          {branches.map((branch) => (
            <SelectionCard
              key={branch.subeId || branch.id}
              active={String(branch.subeId) === selectedBranchId}
              title={branch.name || "Şube"}
              subtitle={[branch.city, `${parseKiosks(branch.kiosks).length} kiosk`].filter(Boolean).join(" · ")}
              onClick={() => { setSelectedBranchId(String(branch.subeId || "")); setError(""); }}
            />
          ))}
        </div>
        <div className="mt-10 grid grid-cols-2 gap-5">
          <button type="button" onClick={() => { setStep("login"); setPendingLogin(null); setBranches([]); }} className="rounded-2xl bg-gray-200 py-6 text-2xl font-bold">Geri</button>
          <button type="button" onClick={goToKioskStep} className="rounded-2xl bg-blue-600 py-6 text-2xl font-extrabold text-white">Kiosk Seç →</button>
        </div>
      </>
    ));
  }

  if (step === "kiosk") {
    return page("Kiosk Seçimi", (
      <>
        <div className="mb-8 rounded-2xl bg-blue-50 p-5 text-center">
          <p className="text-xl text-blue-800">Şube</p>
          <p className="text-3xl font-extrabold text-blue-950">{selectedBranch?.name}</p>
        </div>
        <div className="space-y-5">
          {availableKiosks.map((kiosk) => (
            <SelectionCard key={kiosk} active={selectedKiosk === kiosk} title={kiosk} onClick={() => { setSelectedKiosk(kiosk); setError(""); }} />
          ))}
        </div>
        <div className="mt-10 grid grid-cols-2 gap-5">
          <button type="button" onClick={() => { setStep("branch"); setSelectedKiosk(""); }} className="rounded-2xl bg-gray-200 py-6 text-2xl font-bold">← Şubeler</button>
          <button type="button" onClick={confirmKiosk} className="rounded-2xl bg-emerald-600 py-6 text-2xl font-extrabold text-white">Başlat</button>
        </div>
      </>
    ));
  }

  return page("Hesabınıza Giriş Yapın", (
    <form className="space-y-8" onSubmit={handleSubmit}>
      <div>
        <label className="mb-3 block text-2xl font-bold text-gray-700">Email Adresi</label>
        <input type="email" name="email" required value={formData.email} onChange={(e) => { setFormData((prev) => ({ ...prev, email: e.target.value })); setError(""); }} className="w-full rounded-2xl border-2 border-gray-300 px-6 py-5 text-3xl focus:border-blue-500 focus:outline-none" />
      </div>
      <div>
        <label className="mb-3 block text-2xl font-bold text-gray-700">Şifre</label>
        <input type="password" name="password" required value={formData.password} onChange={(e) => { setFormData((prev) => ({ ...prev, password: e.target.value })); setError(""); }} className="w-full rounded-2xl border-2 border-gray-300 px-6 py-5 text-3xl focus:border-blue-500 focus:outline-none" />
      </div>
      <button type="button" onClick={() => setShowForgotPassword(true)} className="block w-full text-right text-xl font-bold text-blue-600">Şifrenizi mi unuttunuz?</button>
      <button type="submit" disabled={loading} className="w-full rounded-2xl bg-blue-600 py-6 text-3xl font-extrabold text-white disabled:opacity-60">{loading ? "Giriş yapılıyor..." : "Giriş Yap"}</button>
    </form>
  ));
};

export default Login;
